import * as React from 'react';
import { pb } from '@/lib/pb';
import { COLLECTIONS, type Category } from '@/lib/types';
import { slugify } from '@/lib/slug';
import { Btn, Icon } from '@/ui';
import { PageHeader, Card, Spinner, TextField, useToast, pbError } from './ui';

export function Categories() {
  const toast = useToast();
  const [list, setList] = React.useState<Category[] | null>(null);
  const [name, setName] = React.useState('');
  const [editId, setEditId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');

  const reload = React.useCallback(async () => {
    try {
      setList(await pb.collection(COLLECTIONS.categories).getFullList<Category>({ sort: 'name' }));
    } catch (e) { toast.show(pbError(e), 'error'); setList([]); }
  }, [toast]);
  React.useEffect(() => { void reload(); }, [reload]);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await pb.collection(COLLECTIONS.categories).create({ name: n, slug: slugify(n) });
      setName('');
      toast.show('Kategorie angelegt.', 'success');
      await reload();
    } catch (e) { toast.show(pbError(e), 'error'); }
  };

  const saveEdit = async (id: string) => {
    const n = editName.trim();
    if (!n) return;
    try {
      await pb.collection(COLLECTIONS.categories).update(id, { name: n, slug: slugify(n) });
      setEditId(null);
      toast.show('Kategorie aktualisiert.', 'success');
      await reload();
    } catch (e) { toast.show(pbError(e), 'error'); }
  };

  const remove = async (c: Category) => {
    if (!confirm(`Kategorie „${c.name}" löschen?`)) return;
    try {
      await pb.collection(COLLECTIONS.categories).delete(c.id);
      toast.show('Kategorie gelöscht.', 'success');
      await reload();
    } catch (e) { toast.show(pbError(e), 'error'); }
  };

  return (
    <div>
      <PageHeader title="Kategorien" subtitle="Rubriken für Blogbeiträge." />
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <TextField label="Neue Kategorie" value={name} onChange={setName} placeholder="z. B. Ratgeber" />
          </div>
          <Btn variant="primary" size="sm" iconLeft="plus" onClick={add}>Hinzufügen</Btn>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {list === null ? <Spinner /> : list.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--text-200)', fontSize: 14 }}>Noch keine Kategorien.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '12px 18px', width: '55%' }}>
                    {editId === c.id ? (
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                        style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 'var(--radius-sm)', border: '1px solid var(--primary-200)', background: 'var(--surface-page)', color: 'var(--text-100)' }} />
                    ) : (
                      <span style={{ color: 'var(--text-100)', fontWeight: 500 }}>{c.name}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 18px', color: 'var(--text-200)', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{c.slug}</td>
                  <td style={{ padding: '8px 18px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {editId === c.id ? (
                      <>
                        <button onClick={() => saveEdit(c.id)} style={iconBtn('var(--primary-200)')} title="Speichern"><Icon name="check" size={16} /></button>
                        <button onClick={() => setEditId(null)} style={iconBtn('var(--text-200)')} title="Abbrechen"><Icon name="x" size={16} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditId(c.id); setEditName(c.name); }} style={iconBtn('var(--text-200)')} title="Bearbeiten"><Icon name="pencil" size={16} /></button>
                        <button onClick={() => remove(c)} style={iconBtn('#e5484d')} title="Löschen"><Icon name="trash-2" size={16} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function iconBtn(color: string): React.CSSProperties {
  return { background: 'none', border: 'none', cursor: 'pointer', padding: 7, marginLeft: 2, borderRadius: 'var(--radius-sm)', color, display: 'inline-flex', verticalAlign: 'middle' };
}
